import React, { FC, useEffect, useMemo, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";

import { DashboardBunkering } from "./types";

type Props = {
  open: boolean;
  vesselId?: string | number | null;
  initialData?: DashboardBunkering | null;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
};

const API_URL = process.env.REACT_APP_API_URL;

const getEntityId = (value?: any) => {
  if (!value) return "";
  if (typeof value === "string") return value.split("/").filter(Boolean).pop() || value;
  if (value["@id"]) return String(value["@id"]).split("/").filter(Boolean).pop() || value["@id"];
  if (value.id !== undefined && value.id !== null) return String(value.id);

  return "";
};

const getApiIri = (resource: string, id?: string | number | null) => {
  if (!id) return "";
  const stringId = String(id);

  if (stringId.startsWith("/api/")) {
    return stringId;
  }

  if (stringId.startsWith("http://") || stringId.startsWith("https://")) {
    return stringId;
  }

  return `/api/${resource}/${stringId}`;
};

const toDateInputValue = (value?: string | null) => {
  if (!value) return dayjs().format("YYYY-MM-DD");

  const parsed = dayjs(value);

  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
};

const BunkeringDialogForm: FC<Props> = ({
  open,
  vesselId,
  initialData = null,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation();

  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [supplier, setSupplier] = useState("");
  const [fileIri, setFileIri] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const editId = useMemo(() => getEntityId(initialData), [initialData]);
  const editing = Boolean(editId);

  useEffect(() => {
    if (!open) return;

    setAmount(
      initialData?.amount !== null && initialData?.amount !== undefined
        ? String(initialData.amount)
        : ""
    );
    setDate(toDateInputValue(initialData?.date));
    setSupplier(initialData?.supplier || "");
    setFileIri(typeof initialData?.file === "string" ? initialData.file : initialData?.file?.["@id"] || "");
    setError("");
  }, [open, initialData]);

  const submit = async () => {
    const parsedAmount = Number(amount);

    if (!Number.isFinite(parsedAmount) || parsedAmount < 0) {
      setError(t("invalidAmount", "Please enter a valid amount."));
      return;
    }

    if (!date) {
      setError(t("dateRequired", "Date is required."));
      return;
    }

    const vesselIri = getApiIri("vessels", vesselId);

    if (!vesselIri) {
      setError(t("missingVessel", "Missing vessel."));
      return;
    }

    try {
      setSaving(true);
      setError("");

      const payload: any = {
        amount: parsedAmount,
        date: dayjs(date).startOf("day").toISOString(),
        supplier: supplier.trim() || null,
        vessel: vesselIri,
      };

      if (fileIri.trim()) {
        payload.file = fileIri.trim();
      } else {
        payload.file = null;
      }

      if (editing) {
        const iri = initialData?.["@id"] || `/api/vessel_bunkerings/${editId}`;

        await axios({
          method: "put",
          url: `${process.env.REACT_APP_URL || ""}${iri}`,
          headers: {
            "Content-Type": "application/ld+json; charset=utf-8",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          data: payload,
        });
      } else {
        await axios({
          method: "post",
          url: `${API_URL}/vessel_bunkerings`,
          headers: {
            "Content-Type": "application/ld+json; charset=utf-8",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          data: payload,
        });
      }

      await onSaved();
      onClose();
    } catch (exception: any) {
      setError(
        exception?.response?.data?.["hydra:description"] ||
          exception?.response?.data?.detail ||
          exception?.message ||
          t("saveFailed", "Saving failed.")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle sx={{ fontWeight: 950 }}>
        {editing
          ? t("editBunkering", "Edit bunkering")
          : t("addBunkering", "Add bunkering")}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <TextField
            label={t("amountLiters", "Amount in liters")}
            type="number"
            value={amount}
            disabled={saving}
            fullWidth
            inputProps={{
              min: 0,
              step: "0.01",
            }}
            onChange={(event) => setAmount(event.target.value)}
          />

          <TextField
            label={t("date", "Date")}
            type="date"
            value={date}
            disabled={saving}
            fullWidth
            InputLabelProps={{
              shrink: true,
            }}
            onChange={(event) => setDate(event.target.value)}
          />

          <TextField
            label={t("supplier", "Supplier")}
            value={supplier}
            disabled={saving}
            fullWidth
            onChange={(event) => setSupplier(event.target.value)}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          {t("cancel", "Cancel")}
        </Button>

        <Button
          variant="contained"
          onClick={submit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            textTransform: "none",
            fontWeight: 850,
            borderRadius: "10px",
          }}
        >
          {saving ? t("saving", "Saving") : t("save", "Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BunkeringDialogForm;